import { api, type AiTurn, type Chat } from './api';
import { loadMessages } from './archive';
import { fold } from './search';
import { isBoilerplate } from './parser';

/**
 * The loop that lets a model answer questions about an archive it cannot see.
 *
 * The browser is the one holding the key, so the browser is the one that
 * reads. The model is given a catalogue and two things it may ask for; when it
 * asks, this executes the request locally, decrypting exactly what was asked
 * for and nothing else, and posts the result back as the next turn.
 *
 * That ordering is the whole design. The server relays and records rounds; it
 * never sees the archive, never holds a thread, and the feature works the same
 * for an account that keeps its own key as for one whose key we hold.
 */
const TOOLS = [
  {
    name: 'read_conversation',
    description: 'Read messages from one conversation. Returns them in order, ' +
      'oldest first, and always lists everyone who speaks in it plus the ' +
      'group events — who joined, left or was removed. Ask for a stretch ' +
      'rather than the whole thing when you can. Lines beginning with * are ' +
      'events, not something anybody said.',
    parameters: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'The conversation, as the catalogue names it.' },
        from: { type: 'string', description: 'ISO date to start at. Omit for the beginning.' },
        to: { type: 'string', description: 'ISO date to stop at. Omit for the end.' },
        // Named apart from `containing` because conflating them is exactly the
        // mistake: asked for one person's tone, the model reached for
        // `containing: "Glenio"`, got the handful of messages that *mention*
        // him rather than the ones he wrote, and concluded he was not in the
        // group at all.
        sender: { type: 'string',
          description: 'Only messages written by this person. Use this to read ' +
            'what someone said — `containing` searches the message text, not ' +
            'the author.' },
        containing: { type: 'string',
          description: 'Only messages whose text literally contains this ' +
            'string. It is a substring match, not a search for a topic: ' +
            'asking for "pendências" returns only messages where that exact ' +
            'word was typed, and returns nothing when people discussed the ' +
            'matter in other words — which is most of the time. To answer ' +
            'what a conversation was about, or what was decided, or what is ' +
            'outstanding, omit this and read the conversation.' },
      },
      required: ['name'],
    },
  },
];

/** Bounded so one greedy request cannot upload an entire archive. */
const MAX_RETURNED = 1500;
const MAX_ROUNDS = 6;

export interface Catalogue { chat: Chat; title: string }

export async function ask(
  question: string,
  history: AiTurn[],
  catalogue: Catalogue[],
  accountKey: Uint8Array,
  on: { round?: (what: string) => void },
  /** Conversations the person named with "/", if any. */
  focus: string[] = [],
): Promise<{ turns: AiTurn[]; answer: string; read: number }> {
  // Scope is enforced here, not asked for. When somebody picks a conversation
  // the model is shown that one and the executor refuses the rest: a sentence
  // in the prompt is a request, and the model went on reading whatever it
  // liked — which is the opposite of what picking a group means.
  const wanted = focus.map(fold);
  const inScope = ({ title }: Catalogue) =>
    !wanted.length || wanted.some((w) => fold(title) === w || fold(title).includes(w));
  const visible = catalogue.filter(inScope);
  const index = (visible.length ? visible : catalogue).map(({ chat, title }) =>
    `- ${title} · ${chat.messageCount} messages` +
    (chat.lastMessageAt ? ` · last on ${chat.lastMessageAt.slice(0, 10)}` : '')).join('\n');

  const turns: AiTurn[] = [...history];
  // The catalogue rides with the question rather than living in the system
  // prompt, because it changes when a conversation is imported or removed and
  // a stale one sends the model looking for something that is gone.
  // Naming a conversation is an instruction, not a hint. Leaving it in the
  // prose and hoping the model matched the name is how a typo or an accent
  // would send it reading the wrong one.
  // Confinement is not the same as absence, and the model conflated the two:
  // shown one conversation, it answered that the archive had no others and
  // that a group it was asked about did not exist. Both were untrue — the
  // person had simply picked one conversation, and can unpick it.
  const only = focus.length
    ? `\n\nThe person picked ${focus.map((f) => `"${f}"`).join(' and ')} from a ` +
      'longer list, so that is all you can read. The archive holds other ' +
      'conversations; you cannot see them. Asked about one of them, say it is ' +
      'outside the conversation they chose and that removing the chip beside ' +
      'the box asks about everything again — never that the archive does not ' +
      'have it.'
    : '';
  // O que uma ferramenta não encontrou não é uma resposta. Medido: com uma
  // conversa escolhida, o modelo procurava a palavra da pergunta, não a
  // encontrava, e entregava isso — três vezes seguidas, sem nunca ler nada.
  const howToAnswer =
    '\n\nAnswer from what the conversation says, not from whether a word ' +
    'appears in it. A filter that matched nothing is a fact about your ' +
    'search, never an answer to the question: read the conversation and ' +
    'answer from it. Say you could not find something only after having ' +
    'read and looked.';
  turns.push({ role: 'user', parts: [{ text:
    `The archive holds these conversations:\n${index}${only}${howToAnswer}` +
    `\n\nQuestion: ${question}` }] });

  let read = 0;
  let nudged = false;
  // Anything read earlier in this thread counts. `read` is per question, so a
  // follow-up answered from what the previous one fetched looked like a
  // deferral and got nudged — which is how "Alguém ficou chateado?" came back
  // as "Compreendido." instead of an answer.
  const earlier = history.some((t) =>
    (t.parts ?? []).some((p) => (p as { functionResponse?: unknown }).functionResponse));
  for (let round = 0; round < MAX_ROUNDS; round++) {
    const { turn } = await api.ask(turns, TOOLS, read);
    turns.push(turn);

    const calls = turn.parts
      .map((p) => (p as { functionCall?: { name: string; args: Record<string, string> } }).functionCall)
      .filter((c): c is { name: string; args: Record<string, string> } => !!c);

    if (calls.length === 0) {
      const answer = turn.parts.map((p) => (p as { text?: string }).text ?? '').join('').trim();
      // Sometimes it answers "I would need to read the conversations" instead
      // of reading them — measured at one time in six, and always on the
      // vaguest questions. Forcing a tool call would make "hi" decrypt
      // somebody's archive, so the loop insists once instead, and only when
      // nothing has been read yet.
      //
      // The insistence has to carry the escape hatch with it. The first
      // version said plainly "choose a conversation and read it", and the
      // model obeyed — asked for the capital of Australia it decrypted a
      // conversation before declining. A nudge that cannot be declined turns
      // every off-topic question into a read of someone's messages.
      if (read === 0 && !earlier && !nudged) {
        nudged = true;
        // The question goes back with it. The first version was a bare
        // instruction — "choose a conversation and read it" — and the model
        // answered the instruction: "Compreendido." A nudge the model can
        // acknowledge instead of act on is worse than no nudge, because it
        // spends a round and returns nothing.
        turns.push({ role: 'user', parts: [{ text:
          `Answer this: ${question}\n\nIf it is about this archive, choose a ` +
          'conversation from the catalogue and read it — do not ask me which ' +
          'one, you have the list. If it is not about the archive, say so in ' +
          'one sentence and read nothing. Either way, answer; do not reply ' +
          'that you understood.' }] });
        continue;
      }
      return { turns, answer, read };
    }

    const results: Array<Record<string, unknown>> = [];
    for (const call of calls) {
      const args = call.args ?? {};
      // Folded, because "Tamboré" and "tambore" are the same conversation and
      // the model is retyping a name it read rather than copying it.
      const want = fold(args.name ?? '');
      const reachable = visible.length ? visible : catalogue;
      const found = reachable.find((c) => fold(c.title) === want)
        ?? reachable.find((c) => fold(c.title).includes(want));
      on.round?.(found ? `Reading ${found.title}…` : `Looking for ${args.name}…`);

      if (!found?.chat.directoryCid) {
        results.push({ functionResponse: { name: call.name, response: {
          error: focus.length
            ? `Out of scope. "${args.name}" is not one of the conversations ` +
              'you were given, and no other one can be read.'
            : `No conversation called "${args.name}"`,
          available: reachable.map((c) => c.title),
        } } });
        continue;
      }
      // System lines stay. They are how a group records that somebody was
      // added, removed or left, and dropping them made "Glênio was removed
      // from this group" a fact the archive held and nothing could read.
      // Only WhatsApp's own encryption notice goes, which says nothing.
      const all = (await loadMessages(accountKey, found.chat.directoryCid))
        .filter((m) => m.body && !isBoilerplate(m.body));
      // Who is in this conversation, worked out before any filter narrows it.
      // Without this an empty result reads as "that person is not here", which
      // is what it did: asked about one person's tone in a group, it answered
      // that he was not in the group.
      const speakers = [...new Set(all.map((m) => m.sender).filter(Boolean))] as string[];
      // Who joined, left or was removed — the membership history, which is not
      // the same list as who spoke and is sometimes the whole question.
      const events = all.filter((m) => m.kind === 1)
        .map((m) => `[${new Date(m.ts).toISOString().slice(0, 10)}] ${m.body}`);

      let messages = all;
      if (args.from) { const t = Date.parse(args.from); if (!isNaN(t)) messages = messages.filter((m) => m.ts >= t); }
      if (args.to) { const t = Date.parse(args.to); if (!isNaN(t)) messages = messages.filter((m) => m.ts <= t); }
      if (args.sender) {
        const who = fold(args.sender);
        // A group event naming the person is part of the answer to "what did
        // they say and what happened to them", so it survives the filter.
        messages = messages.filter((m) =>
          (m.sender && fold(m.sender).includes(who)) ||
          (m.kind === 1 && m.body && fold(m.body).includes(who)));
      }
      if (args.containing) {
        const needle = fold(args.containing);
        messages = messages.filter((m) => m.body && fold(m.body).includes(needle));
      }
      const said = messages;
      // The tail, when there is too much: the recent end of a conversation is
      // what a question is usually about, and truncating the other end would
      // hide that anything was left out.
      const slice = said.slice(-MAX_RETURNED);
      read += slice.length;
      results.push({ functionResponse: { name: call.name, response: {
        conversation: found.title,
        participants: speakers,
        groupEvents: events.length ? events.slice(-80) : undefined,
        returned: slice.length,
        // Said out loud, because a filter that matches nothing and a person
        // who is not there look identical from the other end.
        // Dizer o que fazer, não só o que aconteceu. A nota antiga descrevia
        // o vazio e o modelo entregava-o como resposta — "não encontrei
        // nenhuma mensagem contendo 'pendências'" — que é um detalhe da
        // ferramenta, não uma resposta a quem perguntou.
        note: slice.length === 0
          ? (args.containing
              ? `Nothing contains "${args.containing}" literally. That is ` +
                `expected when it names a topic rather than a word somebody ` +
                `typed. Call again without \`containing\` and read the ` +
                `${all.length} messages; do not report this empty search as ` +
                `your answer.`
              : `No message matched those filters. Everyone who writes in ` +
                `this conversation is listed in participants; there are ` +
                `${all.length} messages in total.`)
          : undefined,
        truncated: said.length > slice.length
          ? `${said.length - slice.length} earlier messages were not included` : undefined,
        messages: slice.map((m) => {
          const at = new Date(m.ts).toISOString().slice(0, 16);
          // Marked, so an event is never read as something somebody said.
          return m.kind === 1 ? `[${at}] * ${m.body}`
                              : `[${at}] ${m.sender ?? '?'}: ${m.body}`;
        }),
      } } });
    }
    turns.push({ role: 'user', parts: results });
  }

  return { turns, answer: 'I could not get there in the rounds available. Try asking about one conversation at a time.', read };
}
